import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, ChevronDown, ChevronRight, RefreshCw, Printer } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { StatusBadge } from "../StatusBadge";
import { Button } from "../ui/button";
import { Report } from "./print/OutputList";
import { PublishKitPanel } from "./PublishKitPanel";
import { PreflightNext } from "./StageNext";

function Item({ i, go }) {
  return (
    <div className="flex justify-between gap-3 py-2 border-b border-slate-800/60 text-sm" data-testid={`preflight-item-${i.id}`}>
      <div className="min-w-0 [overflow-wrap:anywhere]"><p className="text-slate-200">{i.label}</p>{i.detail && <p className="text-xs text-slate-500">{i.detail}</p>}</div>
      <div className="flex flex-wrap gap-2 items-center shrink-0">
        <StatusBadge status={i.status} />
        {i.tab && i.status !== "PASS" && <button onClick={() => go(i.tab)} className="text-[11px] text-amber-300 hover:underline" data-testid={`preflight-fix-${i.id}`}>Fix</button>}
      </div>
    </div>
  );
}

function Section({ s, go }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="panel p-5 space-y-2 break-inside-avoid" data-testid={`preflight-section-${s.id}`}>
      <div className="flex justify-between items-center"><p className="font-display text-lg">{s.title}</p>
        <div className="flex gap-2 items-center"><StatusBadge status={s.overall} testId={`preflight-overall-${s.id}`} />
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${s.ready ? "bg-emerald-900/50 text-emerald-300" : "bg-rose-900/40 text-rose-300"}`} data-testid={`preflight-ready-${s.id}`}>{s.ready ? "Ready to upload" : "Needs work"}</span></div></div>
      {s.items.map((i) => <Item key={i.id} i={i} go={go} />)}
      {s.reports.map((r) => (
        <div key={r.id}>
          <button onClick={() => setOpen(open === r.id ? null : r.id)} className="w-full flex justify-between items-center gap-2 text-xs text-slate-400 py-2 text-left" data-testid={`preflight-report-toggle-${r.id}`}>
            <span className="flex items-center gap-1 min-w-0">{open === r.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}<span className="font-mono break-all">{r.title}</span></span><span className="shrink-0"><StatusBadge status={r.report.overall} /></span></button>
          {open === r.id && <Report r={r.report} testId={`preflight-report-${r.id}`} />}
        </div>
      ))}
    </div>
  );
}

export default function PreflightTab({ book }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [, setParams] = useSearchParams();
  const load = useCallback(() => api.get(`/books/${book.id}/preflight`).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [book.id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div className="panel p-5 text-sm text-rose-300" data-testid="preflight-error">{err} <button onClick={load} className="underline ml-2">Retry</button></div>;
  if (!d) return <div className="panel p-5 text-sm text-slate-500" data-testid="preflight-loading">Running preflight checks…</div>;
  const go = (tab) => setParams({ tab });
  const ready = d.sections.filter((s) => s.id !== "metadata" && s.ready).length;
  return (
    <div className="space-y-5 preflight-print" data-testid="preflight-tab">
      <div className="panel p-5 flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-3 items-center"><ShieldCheck className="text-amber-400" />
          <div><p className="eyebrow">KDP & publisher preflight{d.approved_version ? ` · approved v${d.approved_version}` : ""}</p>
            <p className="text-sm text-slate-400" data-testid="preflight-summary">{ready} of 4 formats ready to upload. Every report below is the real validation saved with each file; NOT VERIFIED means a check needs your eyes or the publisher's own previewer.</p></div></div>
        <div className="flex gap-2"><StatusBadge status={d.overall} testId="preflight-overall" />
          <Button size="sm" variant="outline" className="border-slate-700" onClick={load} data-testid="preflight-refresh"><RefreshCw size={13} className="mr-1" />Re-check</Button>
          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => window.print()} data-testid="preflight-print"><Printer size={13} className="mr-1" />Print checklist</Button></div>
      </div>
      <PreflightNext book={book} overall={d.overall} />
      <PublishKitPanel book={book} sections={d.sections} />
      <div className="grid xl:grid-cols-2 gap-5 items-start">{d.sections.map((s) => <Section key={s.id} s={s} go={go} />)}</div>
    </div>
  );
}
